/**
 * @namespace core
 */

import { toArray } from 'app/helpers/object'

/**
 * Application global constants
 *
 * @module constants
 * @memberof core
 * @see core
 */

/**
 * Subscriber note types
 *
 * @constant
 * @type {{OTHER: {name: string}, SALE: {name: string}, PAYMENT_ISSUE: {name: string}, CONSULTATION: {name: string}, TECHNICAL_ISSUE: {name: string, subTypes: string[], subTypesName: string}, SUGGESTION: {name: string}, COMPLAINT: {name: string, subTypes: string[], subTypesName: string}}}
 */
export const NOTE_TYPES = {
  TECHNICAL_ISSUE: {
    name: 'Technical issue',
    subTypes: ['DEVICE', 'CLIENT_APP', 'OTHER'],
    subTypesName: 'Issue with'
  },
  PAYMENT_ISSUE: { name: 'Payment issue' },
  CONSULTATION: { name: 'Consultation' },
  COMPLAINT: {
    name: 'Complaint',
    subTypes: ['EMPLOYEE', 'CLIENT_APP', 'ADVERTISING', 'CONTENT', 'OTHER'],
    subTypesName: 'Complaint about'
  },
  SUGGESTION: { name: 'Suggestion' },
  SALE: { name: 'Sale' },
  OTHER: { name: 'Other' }
}

export const NOTE_SUB_TYPES = {
  ADVERTISING: 'Advertising',
  CLIENT_APP: 'Client App',
  CONTENT: 'Content',
  DEVICE: 'Device',
  EMPLOYEE: 'Employee',
  OTHER: 'Other'
}

/**
 *
 * @type {{[periodName:string]: string}}
 */
export const RENT_LENGTH_TYPES = {
  DAY: 'Day(s)',
  MONTH: 'Month(s)'
}
/**
 *
 * @type {{[periodName:string]: string}}
 */
export const VOUCHER_LENGTH_TYPES = {
  DAYS: 'Day(s)',
  MONTHS: 'Month(s)'
}

/**
 *
 * @type {{[periodName:string]: string}}
 */
export const RENT_LENGTH_TYPES2 = {
  Days: 'Day(s)',
  Months: 'Month(s)'
}

/**
 * Skin defaults
 *
 * @constant
 * @type {Object}
 */
export const PREFERENCES_DEFAULTS = {
  skin: {
    sidebar: 'ui-dark'
  }
}

/**
 * Video resolutions
 *
 * @constant
 * @type {String[]}
 */
export const RESOLUTIONS = ['SD', 'HD', 'FHD', '_2K', '_4K']

/**
 * Entity types
 *
 * @constant
 * @type {Object}
 */
export const ENTITY_TYPES = {
  tv: 'TV',
  radio: 'Radio',
  vod: 'VOD',
  series: 'Series',
  channel: 'channel'
}

/**
 * ShowSet types
 *
 * @constant
 * @type {Object}
 */
export const SHOWSET_TYPES = {
  tvShow: 'TV_SHOW',
  season: 'SEASON',
  episode: 'EPISODE'
}

/**
 * ShowSet types labels
 *
 * @constant
 * @type {Object}
 */
export const SHOWSET_TYPE_LABELS = {
  TV_SHOW: 'TV Show',
  SEASON: 'Season',
  EPISODE: 'Episode'
}

/**
 * Listing types, for Packages
 *
 * @constant
 * @type {Object}
 */
export const LISTING_TYPES = {
  'Sequence List': 'Sequence',
  'Group List': 'Group'
}
/**
 * Listing types, for Subscription
 *
 * @constant
 * @type {Object}
 */
export const CHANNEL_LISTING_TYPES = {
  'Sequence List': 'SEQUENCE',
  'Group List': 'GROUP'
}

/**
 * Extensions types, for Extensions management
 *
 * @constant
 * @type {Object}
 */
export const EXTENSION_TYPES = {
  HealthCheck_Node: 'HealthCheck Node',
  Device_Redirect: 'Device Redirect'
}

/**
 * PlaybackTokenGenerator types, for PlaybackTokenGenerator management
 *
 * @constant
 * @type {Object}
 */
export const PLAYBACK_TOKEN_GENERATOR_TYPES = {
  AKAMAI: 'Akamai Token Generator',
  LEVEL3: 'Level3 Token Generator',
  WOWZA: 'Wowza Token Generator',
  CDN77: 'CDN77 Token Generator',
  NIMBLE: 'Nimble Token Generator',
  FLUSSONIC: 'Flussonic Token Generator',
  ONAPP: 'OnApp Token Generator',
  CLOUDFRONT: 'AWS Cloudfront Token Generator',
  STREAMPOOL: 'Streampool Token Generator',
  AKAMAI_EDGIO: 'Multi CDN Token Generator',
  MULTI_CDN: 'Multi CDN Token Generator',
  CACHEFLY: 'CacheFly Token Generator'
}

/**
 * Subscriptions types, for Subscriptions management
 *
 * @constant
 * @type {Object}
 */
export const SUBSCRIPTION_TYPES = {
  Subscription: 'Subscription',
  AddOn: 'Add-on',
  Misc: 'Misc',
  'Subscription ECWID': 'Subscription ECWID'
}

/**
 * Amounts types, for Subscriptions management
 *
 * @constant
 * @type {Object}
 */
export const AMOUNT_TYPES = {
  EXTRA_DEVICE: 'Extra Device',
  TAX_VAT: 'Tax - VAT %',
  EXTRA_CHARGE: 'Extra Charge',
  PRICE: 'Base Price'
}

/**
 * Announcement types
 *
 * @constant
 * @type {Object}
 */
export const ANNOUNCEMENT_TYPE = {
  Announcement: 'Announcement',
  Reminder: 'Reminder',
  Disabled: 'Disabled',
  Activation_TOA: 'Activation TOA'
}

/**
 * Reminder announcement types
 *
 * @constant
 * @type {Object}
 */
export const REMINDER_TYPE = {
  All: 'All Subscribers',
  AutoPay: 'AutoPay Subscribers',
  Non_AutoPay: 'Non AutoPay Subscribers'
}
/**
 * Push Notification statuses
 *
 * @constant
 * @type {Object}
 */
export const PN_STATUSES = {
  SENT_OUT: 'Sent Out',
  DELETED: 'Deleted',
  PENDING: 'Pending',
  FAILED: 'Failed'
}

/**
 * Currency converter types
 *
 * @constant
 * @memberof mwApp/common
 *
 * @ngdoc constant
 * @name mwApp/common.constant:CURRENCY_CONVERTER_TYPES
 *
 * @type {Object}
 */
export const CURRENCY_CONVERTER_TYPES = {
  OPEN_EXCHANGE_RATES: 'Open Exchange Rates',
  FIXER_IO: 'Fixer.io',
  YAHOO_FINANCE: 'Yahoo Finance'
}

/**
 * Payment system types
 *
 * @constant
 * @type {Object}
 */
export const PAYMENT_SYSTEM_TYPES = {
  CASH: 'Cash',
  PAYPAL: 'PayPal',
  AUTHORIZE_NET: 'Authorize.net',
  CHECK: 'Check',
  EXTERNAL_PAYMENTS: 'External Payments',
  // TELR: 'Telr',
  // NEWEBPAY: 'NewebPay',
  GR4VY_GATEWAY: 'Gr4vy',
  GOCARDLESS: 'GoCardless',
  CCPP: '2C2P',
  TRUE_MONEY: 'TrueMoney',
  TRUE_ONLINE: 'TrueOnline',
  TRUE_MOVE_H: 'TrueMove-H',
  AIRWALLEX: 'Airwallex'
}

/**
 * Map of API PaymentSystemType enum -> dashboard "Payment Information Card" setting key.
 * Used to render Setup toggles and Sales Revenue tabs only for Odin-available types.
 *
 * @constant
 * @type {Object}
 */
export const PAYMENT_SYSTEM_TYPE_TO_CARD = {
  CASH: 'CASH_PAYMENTS_CARD',
  AUTHORIZE_NET: 'AUTHORIZE_PAYMENTS_CARD',
  CHECK: 'CHECK_PAYMENTS_CARD',
  EXTERNAL_PAYMENTS: 'EXTERNAL_PAYMENTS_CARD',
  CCPP: 'CCPP_PAYMENTS_CARD',
  GR4VY_GATEWAY: 'GR4VY_PAYMENTS_CARD',
  GOCARDLESS: 'GOCARDLESS_PAYMENTS_CARD',
  TRUE_MONEY: 'TRUE_MONEY_PAYMENTS_CARD',
  TRUE_ONLINE: 'TRUE_ONLINE_PAYMENTS_CARD',
  TRUE_MOVE_H: 'TRUE_MOVE_H_PAYMENTS_CARD',
  AIRWALLEX: 'AIRWALLEX',
  PAYPAL: 'PAYPAL_PAYMENTS_CARD'
}

/**
 * Aggregate payment cards that are never tied to a single PaymentSystemType and
 * therefore are always available regardless of Odin/provider flags.
 *
 * @constant
 * @type {Array<String>}
 */
export const AGGREGATE_PAYMENT_CARDS = ['AUTOPAY_PAYMENTS_CARD', 'ALL_PAYMENTS_CARD']

/**
 * Build a { TYPE: label } map of payment system types, keeping only the types
 * that are available for the provider (as returned by
 * GET api/dashboard/widgets/payments/extra -> paymentSystemTypes).
 * Labels come from PAYMENT_SYSTEM_TYPES; unknown types fall back to the raw enum.
 *
 * @param {Array<String>} [availableTypes] available PaymentSystemType enum strings
 * @returns {Object} filtered map of { TYPE: label }
 */
export function buildAvailablePaymentSystemTypes (availableTypes = []) {
  return (availableTypes || []).reduce((acc, type) => {
    acc[type] = PAYMENT_SYSTEM_TYPES[type] || type
    return acc
  }, {})
}

/**
 * Build the ordered list of dashboard "Payment Information Card" setting keys for
 * the available payment system types. Aggregate cards (Autopay / Total) are
 * always included; system-specific cards appear only when their type is available.
 *
 * @param {Array<String>} [availableTypes] available PaymentSystemType enum strings
 * @returns {Array<String>} card setting keys
 */
export function buildPaymentInformationCards (availableTypes = []) {
  const systemCards = (availableTypes || [])
    .map((type) => PAYMENT_SYSTEM_TYPE_TO_CARD[type])
    .filter(Boolean)
  return [...AGGREGATE_PAYMENT_CARDS, ...systemCards]
}

/**
 * Intersect the selected payment system types with the available ones.
 *
 * @param {Array<String>} [selectedTypes] types selected in Dashboard Setup
 * @param {Array<String>} [availableTypes] types available for the provider
 * @returns {Array<String>} selected types that are also available
 */
export function filterSystemTypes (selectedTypes = [], availableTypes = []) {
  const available = new Set(availableTypes || [])
  return (selectedTypes || []).filter((type) => available.has(type))
}

/**
 * Remove stale/disabled entries from a persisted selection of payment system types.
 * Alias of {@link filterSystemTypes} kept for intent clarity at call sites.
 *
 * @param {Array<String>} [selectedTypes] persisted selection
 * @param {Array<String>} [availableTypes] types available for the provider
 * @returns {Array<String>} sanitized selection
 */
export function sanitizeSystemTypes (selectedTypes = [], availableTypes = []) {
  return filterSystemTypes(selectedTypes, availableTypes)
}

/**
 * Payment system types
 *
 * @constant
 * @type {Object}
 */
export const PRODUCT_STATUSES = {
  ACTIVE: 'Active',
  EXPIRED: 'Expired',
  VOIDED: 'Voided',
  PENDING: 'Pending',
  FAILED: 'Failed',
  VOID_FAILED: 'Void Failed',
  TOA_PENDING: 'TOA Pending'
}

/**
 * External Payment Systems Type list
 *
 * @constant
 * @type {Object}
 */
export const EXTERNAL_PAYMENT_SYSTEM_TYPES = {
  AIRWALLEX: {
    name: 'Airwallex',
    transactionId: 'Transaction ID'
  },
  AUTHORIZE_NET_MANUAL: {
    name: 'Authorize.net Manual',
    transactionId: 'Transaction ID'
  },
  GOCARDLESS: {
    name: 'GoCardless',
    transactionId: 'Transaction ID'
  },
  WESTERN_UNION: {
    name: 'Western Union',
    transactionId: 'Check ID'
  },
  MONEY_GRAM: {
    name: 'MoneyGram',
    transactionId: 'Check ID'
  },
  PAYPAL_EXPRESS: {
    name: 'PayPal Express',
    transactionId: 'Sale ID'
  }
}

/**
 * Payment types
 *
 * @constant
 * @type {Object}
 */
export const PAYMENT_TYPES = {
  Common_Subscription: 'Subscription',
  // Trial_Subscription: 'Trial',
  Custom_Subscription: 'Custom Subscription',
  Accessory: 'Accessory',
  Add_Device: 'Add Device',
  Custom_Charge: 'Custom Charge'
}

/**
 * Product types
 *
 * @constant
 * @type {Object}
 */
export const PRODUCT_TYPES = {
  Add_Device: 'Add Device',
  Add_On: 'Add-on',
  Content_addOn: 'Bundle',
  Content_Item: 'Content Item',
  Custom_Charge: 'Custom Charge',
  Custom_Subscription: 'Custom Subscription',
  Misc: 'Misc',
  Subscription: 'Subscription',
  Accessory: 'Accessory',
  Partner_Product: 'Partner Product',
  Trial_Subscription: 'Trial_Subscription'
}

/**
 * Report types name
 *
 * @constant
 * @type {Object}
 */
export const REPORT_TYPES = {
  // DEVICE: 'Device',
  SUBSCRIBER: 'Subscriber'
}

/**
 * Platform types, used in VODs, Channels and Networks
 *
 * @constant
 * @type {Object[]}
 */
export const PLATFORM_TYPES = [{
  visibleName: 'STB',
  name: 'STB',
  value: 'STB',
  order: 1,
  checked: true
}, {
  visibleName: 'LG',
  name: 'PC',
  value: 'PC',
  order: 2
}, {
  visibleName: 'iOS',
  name: 'iOS',
  value: 'IOS',
  order: 3
}, {
  visibleName: 'tvOS',
  name: 'TVOS',
  value: 'TVOS',
  order: 4
}, {
  visibleName: 'Android Mobile',
  name: 'Android',
  value: 'ANDROID',
  order: 5
}, {
  visibleName: 'SamsungTV',
  name: 'SamsungTV',
  value: 'SAMSUNGTV',
  order: 6
}, {
  visibleName: 'Others/Roku',
  name: 'Others',
  value: 'OTHERS',
  order: 7
}, {
  visibleName: 'WEB',
  name: 'WEB',
  value: 'WEB',
  order: 8
}]

/**
 * Credit cards types and check regular expressions
 *
 * @constant
 * @type {Object}
 */
export const CREDIT_CARD_TYPES = {
  VISA: {
    // starts: 4 length: 13, 16, 19
    re: /^4[0-9]{12}(?:[0-9]{3}){1,2}$/,
    name: 'Visa',
    className: 'fa-cc-visa'
  },
  MASTER_CARD: {
    // starts: 2221-2720, 51-55 length: 16
    re: /^(?:(?:222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)|5[1-5][0-9]{2})[0-9]{12}$/,
    name: 'MasterCard',
    className: 'fa-cc-mastercard'
  },
  AMERICAN_EXPRESS: {
    // starts: 34, 37 length: 15
    re: /^3[47][0-9]{13}$/,
    name: 'American Express',
    className: 'fa-cc-amex'
  },
  DINERS_CLUB: {
    // starts 300-305, 309, 36, 38, 39 length: 14
    re: /^3(?:0[0-59]|[689][0-9])[0-9]{11}$/,
    name: 'Diners Club',
    className: 'fa-cc-diners-club'
  },
  DISCOVER: {
    // starts: 6011, 622126-622925, 644-649, 65 length: 16,19
    re: /^6(?:011[0-9]{2}|22(?:12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[01][0-9]|92[0-5])|4[4-9]|5[0-9]{2})[0-9]{10}(?:[0-9]{3})?$/,
    name: 'Discover',
    className: 'fa-cc-discover'
  },
  JCB: {
    // starts: 3528-3589 length: 16
    re: /^35(?:2[89]|[3-8][0-9])[0-9]{12}$/,
    name: 'JCB',
    className: 'fa-cc-jcb'
  },
  MAESTRO: {
    // starts: 50, 56-69 length: 12-19
    re: /^(?:5(?:0|[6-9])|6[0-9])[0-9]{10,17}$/,
    name: 'Maestro',
    className: 'fas fa-credit-card'
  },
  UNKNOWN: {
    re: null,
    name: 'Unknown',
    className: ''
  }
}

/**
 * Credit card statuses
 *
 * @constant
 * @type {Object}
 */
export const CREDITCARD_STATUSES = {
  OK: 'Ok',
  PENDING: 'Pending',
  INVALID: 'Invalid'
}

/**
 * Subscriber statuses
 *
 * @constant
 * @type {Object}
 */
export const SUBSCRIBER_STATUSES = {
  ACTIVE: {
    name: 'Active',
    color: 'green',
    filter: 'active'
  },
  ON_HOLD: {
    name: 'TOA Pending',
    color: 'yellow',
    filter: 'toa'
  },
  EXPIRED: {
    name: 'Expired',
    color: 'text-danger',
    filter: 'expired'
  },
  PENDING: {
    name: 'Pending',
    color: 'yellow',
    filter: 'pending'
  },
  // DECLINED: {
  //   name: 'Declined',
  //   color: 'yellow',
  //   filter: 'declined'
  // },
  PRE_ACTIVE: {
    name: 'Payment Pending',
    color: 'yellow',
    filter: 'pre_active'
  }
}

/**
 * Payment statuses
 *
 * @constant
 * @type {Object}
 */
export const PAYMENT_STATUSES = {
  ACTIVE: '', // do not show 'Active' status in list
  EXPIRED: 'Expired',
  VOIDED: 'Voided',
  OVERRIDDEN: 'Overridden',
  SKIPPED: 'Skipped',
  ON_HOLD: 'TOA Hold',
  PENDING: 'Pending'
}

/**
 * Payment statuses
 *
 * @constant
 * @type {Object}
 */
export const INAPP_PAYMENT_STATUSES = {
  PAID: 'Paid',
  PAYMENT_FAILED: 'Payment Error',
  PENDING_PAYMENT: 'Pending',
  CANCELLED: 'Cancelled',
  VOID: 'Voided',
  REVERSED: 'Reversed'
}
/**
 * Payment original statuses
 *
 * @constant
 * @type {Object}
 */
export const INAPP_PAYMENT_ORIGINAL_STATUSES = [
  'PENDING_PAYMENT',
  'PAID',
  'PAYMENT_FAILED',
  'CANCELLED',
  'VOID',
  'REVERSED'
]

/**
 * VoucherBatch progress statuses
 *
 * @constant
 * @type {Object}
 */
export const VOUCHERBATCH_STATUSES = {
  IN_PROGRESS: 'In progress',
  DONE: 'Done',
  DONE_WITH_ERRORS: 'Done with errors',
  FAILED: 'Failed',
  UPDATING: 'Updating',
  CREATING: 'Creating',
  DELETING: 'Deleting'
}

/**
 * Filter names by type
 *
 * @constant
 * @type {Object}
 */
export const FILTER_NAMES_BY_TYPES = {
  SUBSCRIBER_STATUS: 'Status',
  SUBSCRIBER_AUTO_PAY: 'Auto pay',
  SUBSCRIBER_DATE: 'Date',
  SUBSCRIBER_NEW_SUBSCRIBERS_DATE_FROM_TO: 'New Subscribers (Date From-To)',
  SUBSCRIBER_NEW_SUBSCRIBERS_DAYS: 'New Subscribers (Last N days)',
  DEVICE_NOT_ASSIGNED: 'Not assigned',
  DEVICE_REDIRECTED: 'Redirected',
  SUBSCRIBER_ENABLED: 'Enabled'
}

/**
 * Report fields
 *
 * @constant
 * @type {Object}
 */
export const REPORT_FIELDS_LIST = {
  ACCOUNT_NUMBER: 'Account number',
  ADDRESS: 'Address',
  CITY: 'City',
  COUNTRY: 'Country',
  CREATION_DATE: 'Created',
  EMAIL: 'E-Mail',
  EXPIRATION_TIME: 'Expiration time',
  EXPIRATION_DATE: 'Expiration date',
  FIRST_NAME: 'First Name',
  LAST_ACCESS_TIME: 'Last access time',
  LAST_ACCESS_DATE: 'Last access date',
  CONVERTED_FROM_VOUCHER: 'Converted from voucher',
  LAST_NAME: 'Last Name',
  PHONE: 'Phone',
  STATE: 'State',
  SUBSCRIPTION_INITIAL_PRICE: 'Subscription Initial Price',
  SUBSCRIPTION_INITIAL_PRICE_CURRENCY: 'Subscription Initial Price Currency',
  SUBSCRIPTION_LENGTH: 'Subscription Length',
  SUBSCRIPTION_LENGTH_ADJUSTMENT: 'Subscription Length Adjustment',
  SUBSCRIPTION_NAME: 'subscription Name',
  SUBSCRIPTION_PLAN: 'Subscription Plan',
  ZIP_CODE: 'Zip code'
}

/**
 * Stream Reporting Method
 *
 * @constant
 * @type {Object}
 */
export const STREAM_REPORT_REPORTING_METHOD = {
  TOP_STREAMING_SUBSCRIBERS: 'Top Streaming Subscribers',
  TOP_WATCHING_SUBSCRIBERS: 'Top Watching Subscribers',
  WATCHING_HABITS: 'Watching Habits',
  CURRENT_STREAMS: 'Current Streams',
  TOP_REQUESTED_STREAMS: 'Stream Request',
  WATCHED_TIME: 'Watched Time',
  SUBSCRIBER_STREAMING_REPORT: 'Subscriber Streaming Report',
  MOST_STREAMED: 'Most Streamed'
}

/**
 * Stream types map
 *
 * @constant
 * @type {Object}
 */
export const STREAM_TYPES = {
  TV: 'TV Channels',
  RADIO: 'Radios',
  VOD: 'Video On Demand',
  CATCHUP: 'Catchup'
}

/**
 * Report layout chart display modes
 *
 * @constant
 * @type {Object}
 */
export const REPORT_DISPLAY_MODE_TYPES = {
  HOUR: 'Hours',
  DAY: 'Days',
  MONTH: 'Months'
}

/**
 * Report layout chart types map
 *
 * @constant
 * @type {Object}
 */
export const REPORT_LAYOUT_CHART_TYPES = {
  3: 'Top 3',
  5: 'Top 5',
  10: 'Top 10',
  20: 'Top 20',
  50: 'Top 50'
}

/**
 * United States' list of states
 *
 * @constant
 * @type {Object[]}
 */
export const US_STATES = [{ code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' }, { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District Of Columbia' }, { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }]

/**
 * Canada's list of states
 *
 * @constant
 * @type {Object[]}
 */
export const CA_STATES = [{ code: 'AB', name: 'Alberta' }, { code: 'BC', name: 'British Columbia' }, { code: 'MB', name: 'Manitoba' }, { code: 'NB', name: 'New Brunswick' }, { code: 'NL', name: 'Newfoundland and Labrador' }, { code: 'NS', name: 'Nova Scotia' }, { code: 'ON', name: 'Ontario' }, { code: 'PE', name: 'Prince Edward Island' }, { code: 'QC', name: 'Quebec' }, { code: 'SK', name: 'Saskatchewan' }, { code: 'NT', name: 'Northwest Territories' }, { code: 'NU', name: 'Nunavut' }, { code: 'YT', name: 'Yukon' }]

/**
 * Fields title by value
 *
 * @constant
 * @type {Object}
 */
export const REPORT_DYNAMIC_FIELDS_TITLES = {
  CITY: 'City',
  COUNTRY: 'Country',
  LANGUAGE: 'Language',
  STATE: 'State',
  STATUS: 'Status',
  ZIPCODE: 'Zip Code',
  DEVICE_MODEL: 'Device Model',
  IP_ADDRESS: 'IP Address',
  LOCATION_COUNTRY: 'Location Country',
  TIMEZONE: 'Timezone'
}

/**
 * Subscribers Reporting Method
 *
 * @constant
 * @type {Object}
 */
export const SUBSCRIBERS_REPORT_REPORTING_METHOD = {
  ACTIVE_SUBSCRIBERS: 'Active Subscribers',
  // TOTAL_SUBSCRIBERS: 'Total Subscribers',
  EXPIRING_SUBSCRIBERS: 'Expiring Subscribers',
  ACTIVATIONS_OF_SUBSCRIBERS: 'Activation of Subscribers',
  PRE_ACTIVE: 'Payment Pending Subscribers'
}

/**
 * Subscribers Reporting types
 *
 * @constant
 * @type {Object}
 */
export const SUBSCRIBERS_REPORT_TYPES = {
  ACTIVE: 'Active Subscribers',
  // TOTAL: 'Total Subscribers',
  EXPIRING: 'Expiring Subscribers',
  ACTIVATIONS: 'Activation of Subscribers',
  PRE_ACTIVE: 'Payment Pending Subscribers'
}

/**
 * Users Reporting types
 *
 * @constant
 * @type {Object}
 */
export const USERS_REPORT_TYPES = {
  ACTIVATIONS: 'Activations Audit'
}

/**
 * Report statuses
 *
 * @constant
 * @type {Object}
 */
export const REPORT_STATUSES = {
  DONE: {
    name: 'Done',
    color: 'green'
  },
  CREATING: {
    name: 'Creating',
    color: 'orange'
  },
  FAILED: {
    name: 'Failed',
    color: 'text-danger'
  },
  PENDING: {
    name: 'Pending',
    color: 'orange'
  },
  DELETED: {
    name: 'Deleted',
    color: 'gray'
  }
}

/**
 * Images types for TV Show
 *
 * @constant
 * @type {Object}
 */
export const TV_SHOW_IMAGES_TYPE = {
  LANDSCAPE: 'LANDSCAPE',
  PORTRAIT: 'PORTRAIT',
  BACKGROUND: 'BACKGROUND'
}

/**
 * Subscription plans
 *
 * @constant
 * @type {Object}
 */
export const SUBSCRIPTION_PLANS_TYPE = {
  LENGTH: 'Length',
  PLANS: 'Plans'
}

/**
 * DRM account types
 *
 * @constant
 * @type {Object}
 */
export const DRM_ACCOUNT_TYPES = {
  EZDRM: 'EZDRM',
  PALLYCON: 'PALLYCON',
  EXPRESS_PLAY: 'EXPRESS_PLAY',
  IRDETO: 'IRDETO'
}

/**
 * DRM account types
 *
 * @constant
 * @type {Object}
 */
export const DRM_ACCOUNT_TYPES_LABELS = {
  [DRM_ACCOUNT_TYPES.EZDRM]: 'EZDRM',
  [DRM_ACCOUNT_TYPES.PALLYCON]: 'Pallycon',
  [DRM_ACCOUNT_TYPES.EXPRESS_PLAY]: 'ExpressPlay',
  [DRM_ACCOUNT_TYPES.IRDETO]: 'Irdeto'
}

/**
 * DRM profile types
 *
 * @constant
 * @type {Object}
 */
export const DRM_PROFILE_TYPES = {
  WIDEVINE: 'WIDEVINE',
  FAIRPLAY: 'FAIRPLAY',
  PLAYREADY: 'PLAYREADY',
  MARLIN: 'MARLIN'
}

/**
 * DRM profile labels of types
 *
 * @constant
 * @type {Object}
 */
export const DRM_PROFILE_TYPES_LABES = {
  [DRM_PROFILE_TYPES.WIDEVINE]: 'Widevine',
  [DRM_PROFILE_TYPES.FAIRPLAY]: 'FairPlay',
  [DRM_PROFILE_TYPES.PLAYREADY]: 'PlayReady',
  [DRM_PROFILE_TYPES.MARLIN]: 'Marlin'
}

/**
 * DRM profile HDCP types
 *
 * @constant
 * @type {Object}
 */
export const HDCP_TYPES = {
  NO_HDCP: 'No HDCP',
  HDCP_V1: 'HDCP 1',
  HDCP_V1_4: 'HDCP 1.4',
  HDCP_V2: 'HDCP 2',
  HDCP_V2_1: 'HDCP 2.1',
  HDCP_V2_2: 'HDCP 2.2',
  HDCP_NO_DIGITAL_OUTPUT: 'No Digital output'
}

/**
 * DRM profile Robustness types
 *
 * @constant
 * @type {Object}
 */
export const ROBUSTNESS_TYPES = {
  SW_SECURE_CRYPTO: 'SW_SECURE_CRYPTO',
  SW_SECURE_DECODE: 'SW_SECURE_DECODE',
  HW_SECURE_CRYPTO: 'HW_SECURE_CRYPTO',
  HW_SECURE_DECODE: 'HW_SECURE_DECODE',
  HW_SECURE_ALL: 'HW_SECURE_ALL'
}

/**
 * Security Level types
 *
 * @constant
 * @type {Object}
 */
export const SECURITY_LEVEL_TYPES = {
  SL150: '150',
  SL2000: '2000',
  SL3000: '3000'
}

/**
 * Streaming Provider types
 *
 * @constant
 * @type {Object}
 */
export const STREAMING_PROVIDER_TYPES = {
  AKAMAI: 'Akamai',
  FLUSSONIC: 'Flussonic',
  WOWZA: 'Wowza',
  NIMBLE: 'Nimble',
  AKAMAI_V2: 'Akamai V2',
  STREAMPOOL: 'Streampool',
  BROADPEAK: 'Broadpeak',
  VELOCIX: 'Velocix',
  TRUE_CDN: 'TrueCDN'
}

/**
 * Live Rewind options
 *
 * @constant
 * @type {Object}
 */
export const LIVE_REWIND_OPTIONS = {
  REWIND: 'Rewind',
  DVR: 'DVR',
  TIMESHIFT: 'Timeshift'
}

/**
 * VOD Set settings types
 *
 * @constant
 * @type {Object}
 */
export const SET_SETTINGS_TYPES = {
  FREE: 'Free',
  PAID: 'Paid'
}
/**
 * Set monetization types
 *
 * @constant
 * @type {Object}
 */
export const MONETIZATION_TYPES = {
  RENT: 'Rent',
  PURCHASE: 'Buy',
  BUY: 'Buy'
}

/**
 * Set pricing policy types
 *
 * @constant
 * @type {Object}
 */
export const PRICING_POLICY_TYPES = {
  COMMON: 'Common',
  CUSTOM: 'Custom'
}

// TODO: MW-5163 need hide PER_ITEM payment Method
/**
 * VOD Set Pricing payment method types
 *
 * @constant
 * @type {Object}
 */
export const SET_PRICING_PAYMENT_METHOD_TYPES = {
  PER_SET: 'PER_SET',
  PER_ITEM: 'PER_ITEM'
}

/**
 * VOD Set Pricing payment method labels
 *
 * @constant
 * @type {Object}
 */
export const SET_PRICING_PAYMENT_METHOD_TYPES_LABELS = {
  [SET_PRICING_PAYMENT_METHOD_TYPES.PER_SET]: 'Per Bundle',
  [SET_PRICING_PAYMENT_METHOD_TYPES.PER_ITEM]: 'Per Item'
}

/**
 * TV Show sets type announcement types
 *
 * @constant
 * @type {Object}
 */
export const TV_SHOW_SETS_TYPE = {
  TV_SHOW: 'TV Shows',
  SEASONS: 'Seasons',
  EPISODES: 'Episodes'
}

/**
 * ACL modes
 *
 * @constant
 * @type {Object}
 */
export const ACL_MODE_TYPES = {
  BLOCK: 'BLOCK',
  ALLOW: 'ALLOW'
}

/**
 * ACL types
 *
 * @constant
 * @type {Object}
 */
export const ACL_TYPES = {
  DEVICE: 'Device',
  CONTENT: 'Content',
  PACKAGE: 'Organization'
}

/**
 * Banner bundle resource types
 *
 * @constant
 * @type {Object}
 */
export const RESOURCE_TYPES = {
  INTERNAL: 'Internal',
  EXTERNAL: 'External',
  WITHOUT_LINK: 'Without link'
}

/**
 * Banner bundle content types
 *
 * @constant
 * @type {Object}
 */
export const CONTENT_TYPES = {
  TV_CHANNEL: 'TV Channel',
  TV_SHOW: 'TV Show',
  MOVIE: 'Movie',
  CATEGORY: 'Category',
  SUBCATEGORY: 'Subcategory',
  CHANNEL: 'Channel',
  VOD: 'VOD'
}

export const CONTENT_TYPES_SETS = {
  TV_SHOW_SET: 'TV Show',
  VOD_SET: 'VOD',
  RADIO_CHANNEL_SET: 'Radio',
  TV_CHANNEL_SET: 'TV Channel',
  CHANNEL_SET: 'Channel',
  MULTI_SET: 'Multi'
}
export const CONTENT_TYPES_SETS_SHORT = {
  ...CONTENT_TYPES_SETS,
  TV_CHANNEL_SET: 'TV'
}

export const CATEGORY_TYPES = {
  CHANNEL: 'Channel',
  CHANNEL_SET: 'Channel Set',
  TV_SHOW: 'TV Show',
  TV_SHOW_SET: 'TV Show Set',
  VOD: 'VOD',
  VOD_SET: 'VOD Set',
  LIVE_EVENT: 'Live Event',
  SEASON: 'Season',
  EPISODE: 'Episode',
  SUBSCRIPTION: 'Subscription',
  ADD_DEVICE: 'Device',
  CUSTOM_CHARGE: 'Custom Churge',
  MISC: 'Misc',
  ADD_ON: 'Add on',
  MULTI_SET: 'Multi Set'
}

/**
 * Image Bundle banner Available types
 *
 * @constant
 * @type {Object}
 */
export const IMAGES_BUNDLE_AVAILABLE_TYPES = {
  MOBILE: 'MOBILE',
  TABLET: 'TABLET',
  TV: 'TV'
}

/**
 * Tag custom error types
 *
 * @constant
 * @type {Object}
 */
export const TAG_CUSTOM_ERROR_TYPES = {
  UNIQUE: 'Tag should be unique within the VOD',
  LIMIT: 'Tag’s limit is 10',
  WORD: 'Tag should be entire word',
  COMMA: 'Tag should not contain commas',
  LENGTH: 'Max length for Tag is 15 symbols'
}

export const SHOW_TAG_CUSTOM_ERROR_TYPES = {
  UNIQUE: 'Tag should be unique within the TV Show',
  LIMIT: 'Tag’s limit is 10'
}

export const FP_POSITIONS_MAP = {
  TOP_LEFT: { name: 'Top Left', class: 'top-left' },
  TOP_CENTER: { name: 'Top Center', class: 'top-center' },
  TOP_RIGHT: { name: 'Top Right', class: 'top-right' },
  CENTER_LEFT: { name: 'Center Left', class: 'center-left' },
  CENTER: { name: 'Center', class: 'center' },
  CENTER_RIGHT: { name: 'Center Right', class: 'center-right' },
  BOTTOM_LEFT: { name: 'Lower Left', class: 'lower-left' },
  BOTTOM_CENTER: { name: 'Lower Center', class: 'lower-center' },
  BOTTOM_RIGHT: { name: 'Lower Right', class: 'lower-right' }
}

export const FP_POSITIONS = [
  'TOP_LEFT',
  'TOP_CENTER',
  'TOP_RIGHT',
  'CENTER_LEFT',
  'CENTER',
  'CENTER_RIGHT',
  'BOTTOM_LEFT',
  'BOTTOM_CENTER',
  'BOTTOM_RIGHT']

export const FP_FONT_TYPES_MAP = {
  IBM_PLEX_SANS: 'IBM Plex Sans',
  IBM_PLEX_SANS_CONDENSED: 'IBM Plex Sans Condensed',
  PRODUCT_SANS: 'Product Sans',
  INTER: 'Inter',
  WORK_SANS: 'Work Sans',
  MONTSERRAT: 'Montserrat'
}

export const FP_FONT_TYPES = [
  'IBM_PLEX_SANS',
  'IBM_PLEX_SANS_CONDENSED',
  'PRODUCT_SANS',
  'INTER',
  'WORK_SANS',
  'MONTSERRAT']

export const FP_FONT_COLOURS = ['#7DC2F4', '#EF90BC', '#F4966E', '#A2D179', '#84E9D7', '#F9F9F9', '#17283E']

/**
 * VOD Set settings monetization types
 *
 * @constant
 * @type {Object}
 */
export const SET_PRICING_MONETIZATION_TYPES = {
  RENT: 'RENT',
  PURCHASE: 'PURCHASE'
}

/**
 * VOD Set settings monetization labels
 *
 * @constant
 * @type {Object}
 */
export const SET_PRICING_MONETIZATION_TYPES_LABELS = {
  [SET_PRICING_MONETIZATION_TYPES.RENT]: 'Rent',
  [SET_PRICING_MONETIZATION_TYPES.PURCHASE]: 'Buy'
}

/**
 * Providers for branding
 *
 * @type {Object}
 */
export const PROVIDERS_CONST = {
  Nora: {
    name: 'Nora'
  },
  TvDip: {
    name: 'Tv Dip'
  },
  Streeme: {
    name: 'Streeme'
  },
  CDNX: {
    name: 'Middleware',
    hideSidebarBottomBranding: true
  }
}

/**
 * VOD Set settings policy types
 *
 * @constant
 * @type {Object}
 */
export const SET_PRICING_POLICY_TYPES = {
  COMMON: 'COMMON',
  CUSTOM: 'CUSTOM'
}

/**
 * VOD Set settings policy text types
 *
 * @constant
 * @type {Object}
 */
export const SET_PRICING_POLICY_TEXT_TYPES = {
  COMMON: 'Common',
  CUSTOM: 'Custom'
}

/**
 * Setrix stream types
 *
 * @constant
 * @type {{RELAY: string, ENCODING: string}}
 */
export const SETRIX_STREAM_TYPES = {
  ENCODING: 'ENCODING', RELAY: 'RELAY'
}

/**
 * Setrix stream types labels
 *
 * @constant
 * @type {{}}
 */
export const SETRIX_STREAM_TYPES_LABELS = {
  [SETRIX_STREAM_TYPES.ENCODING]: 'Encoding',
  [SETRIX_STREAM_TYPES.RELAY]: 'Relay'
}

/**
 * Video types
 *
 * @constant
 * @type {{RELAY: string, ENCODING: string}}
 */
export const SETRIX_VIDEO_TYPES = {
  VOD: 'VOD', LIVE: 'LIVE'
}

/**
 * Video types labels
 *
 * @constant
 * @type {{}}
 */
export const SETRIX_VIDEO_TYPES_LABELS = {
  [SETRIX_VIDEO_TYPES.VOD]: 'VOD',
  [SETRIX_VIDEO_TYPES.LIVE]: 'Live'
}

/**
 * Input types
 *
 * @constant
 * @type {{RELAY: string, ENCODING: string}}
 */
export const SETRIX_INPUT_TYPES = {
  DEV: 'DEV',
  DVB: 'DVB',
  MULTI_VIEWER: 'MULTI_VIEWER',
  PLAYLIST: 'PLAYLIST',
  REMOTE_CACHING: 'REMOTE_CACHING',
  TEST: 'TEST',
  TIME_SHIFT_RECORDER: 'TIME_SHIFT_RECORDER',
  URI: 'URI'

}

/**
 * Input types labels
 *
 * @constant
 * @type {{}}
 */
export const SETRIX_INPUT_TYPES_LABELS = {
  [SETRIX_INPUT_TYPES.DEV]: 'Dev',
  [SETRIX_INPUT_TYPES.DVB]: 'DVB',
  [SETRIX_INPUT_TYPES.MULTI_VIEWER]: 'Multi Viewer',
  [SETRIX_INPUT_TYPES.PLAYLIST]: 'Playlist',
  [SETRIX_INPUT_TYPES.REMOTE_CACHING]: 'Remote Caching',
  [SETRIX_INPUT_TYPES.TEST]: 'Test',
  [SETRIX_INPUT_TYPES.TIME_SHIFT_RECORDER]: 'Time Shift Recorder',
  [SETRIX_INPUT_TYPES.URI]: 'URI'
}

/**
 * Output uri types
 *
 * @constant
 * @type {{RELAY: string, ENCODING: string}}
 */
export const SETRIX_OUTPUT_URI_TYPES = {
  RTMP: 'RTMP',
  HLS_PULL: 'HLS_PULL',
  HLS_PUSH: 'HLS_PUSH',
  DASH_PULL: 'DASH_PULL',
  DASH_PUSH: 'DASH_PUSH',
  HTTP_TS: 'HTTP_TS',
  HTTP: 'HTTP',
  UDP: 'UDP',
  SRT: 'SRT',
  TCP: 'TCP',
  PLAYBACK: 'PLAYBACK',
  FILE: 'FILE',
  TEST: 'TEST'
}

/**
 * Output uri types labels
 *
 * @constant
 * @type {{}}
 */
export const SETRIX_OUTPUT_URI_TYPES_LABELS = {
  [SETRIX_OUTPUT_URI_TYPES.RTMP]: 'RTMP',
  [SETRIX_OUTPUT_URI_TYPES.HLS_PULL]: 'HLS Pull',
  [SETRIX_OUTPUT_URI_TYPES.HLS_PUSH]: 'HLS Push',
  [SETRIX_OUTPUT_URI_TYPES.DASH_PULL]: 'DASH Pull',
  [SETRIX_OUTPUT_URI_TYPES.DASH_PUSH]: 'DASH Push',
  [SETRIX_OUTPUT_URI_TYPES.HTTP_TS]: 'HTTP TS',
  [SETRIX_OUTPUT_URI_TYPES.HTTP]: 'HTTP',
  [SETRIX_OUTPUT_URI_TYPES.UDP]: 'UDP',
  [SETRIX_OUTPUT_URI_TYPES.SRT]: 'SRT',
  [SETRIX_OUTPUT_URI_TYPES.TCP]: 'TCP',
  [SETRIX_OUTPUT_URI_TYPES.PLAYBACK]: 'Playback',
  [SETRIX_OUTPUT_URI_TYPES.FILE]: 'File',
  [SETRIX_OUTPUT_URI_TYPES.TEST]: 'Test'
}

/**
 * Output uri types
 *
 * @constant
 * @type {{RELAY: string, ENCODING: string}}
 */
export const SETRIX_DRM_SYSTEMS_TYPES = {
  FAIRPLAY: 'FAIRPLAY',
  WIDEVINE: 'WIDEVINE',
  PLAYREADY: 'PLAYREADY',
  MARLIN: 'MARLIN'
}

/**
 * Input types labels
 *
 * @constant
 * @type {{}}
 */
export const SETRIX_DRM_SYSTEMS_TYPES_LABELS = {
  [SETRIX_DRM_SYSTEMS_TYPES.FAIRPLAY]: 'Fairplay',
  [SETRIX_DRM_SYSTEMS_TYPES.WIDEVINE]: 'Widevine',
  [SETRIX_DRM_SYSTEMS_TYPES.PLAYREADY]: 'PlayReady',
  [SETRIX_DRM_SYSTEMS_TYPES.MARLIN]: 'Marlin'

}

/**
 * Stream comments types
 *
 * @constant
 * @type {Object}
 */
export const SETRIX_STREAM_COMMENT_TYPES = {
  NOTICE: 'NOTICE',
  WARNING: 'WARNING',
  CRITICAL: 'CRITICAL'
}

/**
 * Stream comments types
 *
 * @constant
 * @type {Object}
 */
export const SETRIX_STREAM_WORK_STATUS_TYPES = {
  NEW: 'NEW',
  INITIALIZING: 'INITIALIZING',
  STARTED: 'STARTED',
  READY: 'READY',
  STREAMING: 'STREAMING',
  TRANSCODING: 'TRANSCODING',
  FROZEN: 'FROZEN',
  WAITING: 'WAITING',
  UNSTABLE_INPUT: 'UNSTABLE_INPUT',
  DONE: 'DONE',
  UNKNOWN: 'UNKNOWN'
}

/**
 * Stream comments types labels
 *
 * @constant
 * @type {Object}
 */
export const SETRIX_STREAM_WORK_STATUS_TYPES_LABELS = {
  [SETRIX_STREAM_WORK_STATUS_TYPES.NEW]: 'New',
  [SETRIX_STREAM_WORK_STATUS_TYPES.INITIALIZING]: 'Initializing',
  [SETRIX_STREAM_WORK_STATUS_TYPES.STARTED]: 'Started',
  [SETRIX_STREAM_WORK_STATUS_TYPES.READY]: 'Ready',
  [SETRIX_STREAM_WORK_STATUS_TYPES.STREAMING]: 'Streaming',
  [SETRIX_STREAM_WORK_STATUS_TYPES.TRANSCODING]: 'Transcoding',
  [SETRIX_STREAM_WORK_STATUS_TYPES.FROZEN]: 'Frozen',
  [SETRIX_STREAM_WORK_STATUS_TYPES.WAITING]: 'Waiting',
  [SETRIX_STREAM_WORK_STATUS_TYPES.UNSTABLE_INPUT]: 'Unstable Input',
  [SETRIX_STREAM_WORK_STATUS_TYPES.DONE]: 'Done',
  [SETRIX_STREAM_WORK_STATUS_TYPES.UNKNOWN]: 'N/A'
}

/**************************************************************************************************
 *
 * ██╗      ██████╗  █████╗ ██████╗     ███████╗██████╗  ██████╗ ███╗   ███╗     █████╗ ██████╗ ██╗
 * ██║     ██╔═══██╗██╔══██╗██╔══██╗    ██╔════╝██╔══██╗██╔═══██╗████╗ ████║    ██╔══██╗██╔══██╗██║
 * ██║     ██║   ██║███████║██║  ██║    █████╗  ██████╔╝██║   ██║██╔████╔██║    ███████║██████╔╝██║
 * ██║     ██║   ██║██╔══██║██║  ██║    ██╔══╝  ██╔══██╗██║   ██║██║╚██╔╝██║    ██╔══██║██╔═══╝ ██║
 * ███████╗╚██████╔╝██║  ██║██████╔╝    ██║     ██║  ██║╚██████╔╝██║ ╚═╝ ██║    ██║  ██║██║     ██║
 * ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝     ╚═╝     ╚═╝  ╚═╝ ╚═════╝ ╚═╝     ╚═╝    ╚═╝  ╚═╝╚═╝     ╚═╝
 */

// $http service from angular
let $http

/**
 * Helper function to memoize loaded data.
 * Also, will retrieve and inject $http service to function
 *
 * @param  {Function} fn - function to memoize (arguments are not supported)
 * @return {Function} memoized function, that will run original function once,
 *                    and then returns data without actually running original function
 */
function memoize$ (fn) {
  let done, data
  return () => {
    // get $http service from angular
    // do not use `angular.injector(['ng']).get('$http')`
    // because this will create _new_ instance of injector,
    // while we want to use injector from application (with interceptors and decorators)
    $http = $http || angular.element(document.body).injector().get('$http')
    return done ? data : (done = true, data = fn($http)) // eslint-disable-line no-return-assign
  }
}

/**
 * Helper function to get data from $http `response`
 *
 * @param  {Object} response
 * @return {*}
 */
const data = (response) =>
  response && response.data !== undefined
    ? response.data
    : null

/**
 * World timezones
 *
 * @function
 * @return {Promise}
 */
export const GET_TIMEZONES = memoize$(
  ($http) =>
    $http.get('api/info/timezones')
      .then(data)
)

/**
 * World languages
 *
 * @function
 * @return {Promise}
 */
export const GET_LANGUAGES = memoize$(
  ($http) =>
    $http.get('api/info/languages')
      .then(data)
)

/**
 * Locale tags that should appear at the top of the language dropdown in the
 * content-localization modal. Anything not in this list is alphabetized.
 *
 * @constant
 * @type {string[]}
 */
export const POPULAR_LOCALES = ['en', 'es', 'fr', 'de', 'pt', 'ru', 'zh', 'ja']

/**
 * World countries
 *
 * @function
 * @return {Promise}
 */
export const GET_COUNTRIES = memoize$(
  ($http) =>
    $http.get('api/info/countries')
      .then(data)
      .then(toArray('code', 'name'))
)

/**
 * World currencies
 *
 * @function
 * @return {Promise}
 */
export const GET_CURRENCIES = memoize$(
  ($http) =>
    $http.get('api/info/currencies')
      .then(data)
      .then(toArray('code', 'symbol'))
)

/**
 * Purchase statuses
 *
 * @constant
 * @type {Object}
 */
export const PURCHASE_STATUSES = {
  ACTIVE: 'Active',
  CANCELLED: 'Cancelled',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  PENDING: 'Pending',
  PENDING_TOA: 'TOA Pending',
  PRE_ACTIVE: 'Payment Pending',
  UPCOMING: 'Upcoming',
  VOIDED: 'Voided'
}

export const PURCHASE_STATUSES_FOR_FINANCIAL_REPORT = {
  ACTIVE: 'Active',
  CANCELLED: 'Cancelled',
  // DECLINED: 'Declined',
  EXPIRED: 'Expired',
  INACTIVE: 'Inactive',
  PENDING: 'Pending',
  // PRE_ACTIVE: 'Payment Pending',
  TOA_PENDING: 'TOA Pending',
  UPCOMING: 'Upcoming',
  VOIDED: 'Voided/Refunded'
}

/**
 * Purchase statuses
 *
 * @constant
 * @type {Object}
 */
export const PARTNER_PRODUCT_STATUSES = {
  ACTIVE: 'Active',
  EXPIRED: 'Expired',
  VOIDED: 'Voided',
  PENDING: 'Pending',
  FAILED: 'Failed',
  VOID_FAILED: 'Void Failed',
  VOID_PENDING: 'Void Pending',
  EXPIRATION_PENDING: 'Expiration Pending'
}

/**
 * Source Types for purchase
 *
 * @constant
 * @type {Object}
 */
export const SOURCE_TYPES = {
  IN_APP: 'Client App',
  EXTERNAL: 'External Systems',
  ADMIN_PANEL: 'Nora'
}

/**
 *
 * Yandex Metrika Counter
 * @type {number}
 */
export const YM_COUNTER = 91203292

export const URL_PATTERN = '^(rtmp|ws|wss|ftp|http|https|udp|rtp):\\/\\/(\\w+:{0,1}\\w*@)?(\\S+)(:[0-9]+)?(\\/|\\/([\\w#!:.?+=&%@!\\-\\/]))?$'

// Stricter but still simple (linear-time) pattern:
// - Protocol: rtmp|ws|wss|ftp|http|https|udp|rtp
// - Host:
//    * either a pure {{VAR}}
//    * or a regular domain with at least one dot: label.label[.label...]
// - Optional port :NNNNN
// - Optional path, without spaces
// - Spaces in URL are forbidden, {{VAR}} is allowed in host (as a whole), and in path as part of the string
export const URL_PATTERN_FOR_TEMPLATES =
  '^(rtmp|ws|wss|ftp|http|https|udp|rtp):\\/\\/(?:{{[^{}\\s]+}}|[^\\s\\/.]+(?:\\.[^\\s\\/]+)+)(?::[0-9]{1,5})?(?:\\/[^\\s]*)?$'

export const ACCESSORY_TYPES = { BOX: 'Box', EQUIPMENT: 'Equipment', FEE: 'Fee', SHIPPING: 'Shipping' }
export const ACCESSORY_TYPES_FOR_FILTERS = { ...ACCESSORY_TYPES, NO_TYPE: 'No Type' }

export const SUBSCRIPTION_PRODUCT_TYPES_FOR_DISCOUNT = { SUBSCRIPTION: 'Subscription', CONTENT_ADDON: 'Content Add-ons', ACCESSORY: 'Accessories' }

export const CONTENT_SET_TYPES = {
  channelSets: { link: 'channelSets', name: 'Channel Sets' },
  vodSets: { link: 'vodSets', name: 'VOD Sets' },
  tvShowSets: { link: 'tvShowSets', name: 'TV Show Sets' },
  liveEventSets: { link: 'liveEventSets', name: 'Live Event Sets' },
  multiSets: { link: 'multiSets' }
}

export const REPORT_FINANCIAL_PERIODS = {
  '1DAYS': { name: 'Today', periodCount: 1, periodType: 'DAYS' },
  '2DAYS': { name: '2 Days', periodCount: 2, periodType: 'DAYS' },
  '3DAYS': { name: '3 Days', periodCount: 3, periodType: 'DAYS' },
  '7DAYS': { name: '1 Week', periodCount: 7, periodType: 'DAYS' },
  '14DAYS': { name: '2 Weeks', periodCount: 14, periodType: 'DAYS' },
  '1MONTHS': { name: '1 Month', periodCount: 1, periodType: 'MONTHS' },
  '3MONTHS': { name: '3 Months', periodCount: 3, periodType: 'MONTHS' },
  '6MONTHS': { name: '6 Months', periodCount: 6, periodType: 'MONTHS' },
  '1YEARS': { name: '1 Year', periodCount: 1, periodType: 'YEARS' },
  CUSTOM: { name: 'Custom', periodCount: null, periodType: 'CUSTOM' }
}

export const REPORT_FINANCIAL_PRODUCT_TYPES = {
  TV_SHOW: 'Tv Shows',
  TV_SHOW_SET: 'Tv Show Sets',
  SEASON: 'Seasons',
  EPISODE: 'Episodes',
  VOD: 'VODs',
  VOD_SET: 'VOD Sets',
  CHANNEL: 'Channels',
  CHANNEL_SET: 'Channel Sets',
  LIVE_EVENT: 'Live Events',
  MULTI_SET: 'Multi Sets',
  SUBSCRIPTION: 'Subscriptions',
  ADD_DEVICE: 'Add Devices',
  CUSTOM_CHARGE: 'Custom Charges',
  ACCESSORY: 'Accessories',
  PARTNER_PRODUCT: 'Partner Products',
  MISC: 'Misc',
  ADD_ON: 'Add-ons',
  CUSTOM_SUBSCRIPTION: 'Custom Subscription'
}

export const PLATFORM_PREFIXES = {
  tvs: 'trueid:'
}

export const SSAI_CONTENT_TYPES = {
  vods: { request: 'VODS', name: 'VODs' },
  lives: { request: 'LIVE_CATCHUPS', name: 'Live & Catch-ups' }
}

export const GEOAPIFY_CONFIDENCE_THRESHOLD = 0.5

export const PAYMENT_SYSTEM_TYPE_NAME = {
  CREDIT_CARD: 'Credit Card',
  GOCARDLESS: 'Gocardless',
  GOOGLEPAY: 'Google Pay',
  APPLEPAY: 'Apple Pay',
  MULTIPAGO: 'Multipago',
  PAYPAL: 'PayPal',
  RAZORPAY: 'Razorpay',
  TMN_WALLET: 'Tmn Wallet'
}

export const REPORT_FIELDS = {
  CONVERTED_AFTER_TRIAL_DAYS: 'Converted after trial',
  DISCOUNT_AMOUNT: 'Discount Amount',
  DISCOUNT_CAMPAIGN: 'Discount Campaign',
  DISCOUNT_CODE: 'Discount Code',
  DISCOUNT_TYPE: 'Discount Type',
  DISCOUNT_VALUE: 'Discount Value',
  ORGANIZATION_NAME: 'Organization',
  PURCHASE_EVENT_DATE: 'Event Date',
  PURCHASE_ITEM_AMOUNT: 'Amount',
  PURCHASE_ITEM_AUTOPAY: 'Autopay',
  PURCHASE_ITEM_AVAILABLE_FROM: 'Active From Date',
  PURCHASE_ITEM_AVAILABLE_TO: 'Active To Date',
  PURCHASE_ITEM_CATEGORY: 'Product type',
  PURCHASE_ITEM_CURRENCY: 'Currency',
  PURCHASE_ITEM_EXTRA_LENGTH: 'Length adjustment',
  PURCHASE_ITEM_LENGTH: 'Length',
  PURCHASE_ITEM_NAME: 'Product Name',
  PURCHASE_ITEM_PRORATED: 'Prorated',
  PURCHASE_ITEM_STATUS: 'Status',
  PURCHASE_ITEM_SUBSCRIPTION_PLAN_NAME: 'Subscription Plan Name',
  PURCHASE_ITEM_TRIAL: 'Is trial',
  PURCHASE_NUMBER: 'Payment Number', // (transaction Id in Nora)',
  PURCHASE_PAYMENT_SYSTEM: 'Payment Service',
  PURCHASE_TRANSACTION_ID: 'Transaction ID', // (in external system)',
  RECURRING_DISCOUNT: 'Recurring',
  SUBSCRIBER_ACCOUNT_NUMBER: 'Subscriber account number',
  SUBSCRIBER_COUNTRY: 'Country',
  SUBSCRIBER_EMAIL: 'E-mail',
  SUBSCRIBER_FIRSTNAME: 'Subscriber First Name',
  SUBSCRIBER_LASTNAME: 'Subscriber Last Name',
  SUBSCRIBER_PHONE: 'Phone',
  TARGETED_DISCOUNT: 'Targeted Product',
  USER: 'User'
}

/** ISO2 (lowercase) -> display label for MOTN External Content default country select. */
export const MOVIE_OF_THE_NIGHT_COUNTRIES = {
  md: 'Moldova',
  az: 'Azerbaijan',
  tr: 'Turkey',
  mk: 'North Macedonia',
  nz: 'New Zealand',
  jp: 'Japan',
  hr: 'Croatia',
  pt: 'Portugal',
  gr: 'Greece',
  nl: 'Netherlands',
  th: 'Thailand',
  gb: 'United Kingdom',
  id: 'Indonesia',
  ec: 'Ecuador',
  is: 'Iceland',
  vn: 'Vietnam',
  de: 'Germany',
  hu: 'Hungary',
  sk: 'Slovakia',
  at: 'Austria',
  ph: 'Philippines',
  kr: 'South Korea',
  my: 'Malaysia',
  si: 'Slovenia',
  cz: 'Czech Republic',
  us: 'United States',
  es: 'Spain',
  rs: 'Serbia',
  ro: 'Romania',
  pa: 'Panama',
  ae: 'United Emirates',
  il: 'Israel',
  it: 'Italy',
  fi: 'Finland',
  cy: 'Cyprus',
  ch: 'Switzerland',
  pl: 'Poland',
  ie: 'Ireland',
  dk: 'Denmark',
  ar: 'Argentina',
  br: 'Brazil',
  ee: 'Estonia',
  hk: 'Hong Kong',
  pe: 'Peru',
  co: 'Colombia',
  za: 'South Africa',
  ca: 'Canada',
  be: 'Belgium',
  in: 'India',
  ru: 'Russia',
  se: 'Sweden',
  fr: 'France',
  au: 'Australia',
  bg: 'Bulgaria',
  mx: 'Mexico',
  cl: 'Chile',
  sg: 'Singapore',
  ua: 'Ukraine',
  no: 'Norway',
  lt: 'Lithuania',
  bo: 'Bolivia',
  cr: 'Costa Rica',
  gt: 'Guatemala',
  hn: 'Honduras',
  sv: 'El Salvador',
  ve: 'Venezuela'
}
